// external
import { useContext } from "react";
import { toast } from "react-toastify";

// state managing/security
import { FaoResultsContext } from "context/FaoResultsContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { ClientContext } from "context/ClientContext";

// api
import apiRoot from "../../../api/apiRoot";

/**
 * useNonModeledHandlers Hook
 *
 * This hook provides all the business logic and event handlers for managing
 * non-modeled data interactions in the FAO results interface. It handles:
 *
 * Key Responsibilities:
 * - Input change handling for enrollment and discount rate fields
 * - Reset functionality for individual groups and all data
 * - Data validation and change detection
 * - Save operations with proper error handling and user feedback
 * - State management for saving/saved indicators
 *
 * Business Logic:
 * - Tracks which groups have been modified compared to base data
 * - Recalculates financial metrics when enrollment or discount rates change
 * - Provides reset functionality to revert to original values
 * - Provides saving functionality
 */
function useNonModeledHandlers() {
  const { modelConfig } = useContext(FaoBuilderContext);
  const { currentClientData } = useContext(ClientContext);
  const {
    costOfAttendance,
    nonModeledDispatch,
    nonModeledData,
    setIsSaving,
    setIsSaved,
  } = useContext(FaoResultsContext);

  /**
   * Determines if a specific group has been adjusted from its base values
   * Compares both enrollment and tuition discount rate to detect changes
   *
   * @param {Object} item - The group item to check
   * @returns {boolean} True if the group has been modified
   */
  const isGroupAdjusted = (item) => {
    const { base } = nonModeledData;
    const baseItem = base.find((cur) => cur.group === item.group);
    return (
      baseItem?.estEnrollment !== item?.estEnrollment ||
      baseItem?.estTuitionDiscountRate !== item.estTuitionDiscountRate
    );
  };

  /**
   * Checks if any group in the entire non modeled population has been adjusted
   * Used for UI indicators and validation
   *
   * @returns {boolean} True if any adjustments have been made
   */
  const isPopulationAdjusted = () => {
    const { adjustedValues } = nonModeledData;
    return adjustedValues.some((item) => isGroupAdjusted(item));
  };

  /**
   * Resets a specific group to its original base values
   * Triggers the reset_group reducer action
   *
   * @param {Object} item - The group item to reset
   */
  const handleReset = (item) => {
    nonModeledDispatch({
      type: "reset_group",
      item: item,
    });
  };

  /**
   * Resets all groups to their original base values
   * Triggers the reset_adjusted reducer action to clear all modifications
   */
  const handleResetAll = () => {
    nonModeledDispatch({
      type: "reset_adjusted",
    });
  };

  /**
   * Handles enrollment input changes and triggers financial recalculations
   * When enrollment changes, it affects revenue and aid calculations
   *
   * @param {Object} newNonModeledItem - Updated item with new enrollment value
   */
  const handleEnrollInputChange = (newNonModeledItem) => {
    nonModeledDispatch({
      type: "update_group",
      item: newNonModeledItem,
      tuition: costOfAttendance[0].currentYear, // Current year tuition for calculations
    });
  };

  /**
   * Handles discount rate input changes and triggers financial recalculations
   * When discount rate changes, it affects aid amounts and net revenue
   *
   * @param {Object} newNonModeledItem - Updated item with new discount rate
   */
  const handleDiscountRateInputChange = (newNonModeledItem) => {
    nonModeledDispatch({
      type: "update_group",
      item: newNonModeledItem,
      tuition: costOfAttendance[0].currentYear, // Current year tuition for calculations
    });
  };

  /**
   * Main input change handler that routes to specific field handlers
   * Parses the new value as a float and updates the appropriate field
   *
   * @param {Object} params - Input change parameters
   * @param {Object} params.nonModeledItem - The original item being modified
   * @param {string} params.newValue - The new value as string from input
   * @param {string} params.field - The field being changed (estEnrollment or estTuitionDiscountRate)
   */
  const handleNonModeledInputChange = ({ nonModeledItem, newValue, field }) => {
    // Create updated item with parsed numeric value
    const newNonModeledItem = {
      ...nonModeledItem,
      [field]: parseFloat(newValue),
    };

    // Route to appropriate handler based on field type
    if (field === "estEnrollment") {
      handleEnrollInputChange(newNonModeledItem);
    } else if (field === "estTuitionDiscountRate") {
      handleDiscountRateInputChange(newNonModeledItem);
    }
  };

  /**
   * Handles saving non-modeled adjustments to the backend
   *
   * Features:
   * - Sets loading states for UI feedback
   * - Uses toast notifications for user feedback
   * - Updates existing scenario via API
   * - Manages saved state indicators
   * - Proper error handling with console logging
   *
   * The save operation updates the existing scenario record with the
   * current adjusted non-modeled population data.
   */
  const handleNonModeledSave = async () => {
    // Set saving state for UI loading indicators
    setIsSaving((prev) => ({ ...prev, nonModeledData: true }));

    try {
      // Configure toast messages for different states
      const toastMessages = {
        pending: "Saving Non-Modeled Adjustments",
        success: "SUCCESS - Non-Modeled Adjustments saved",
        error: "Non-Modeled Adjustments not saved",
      };

      // Get the scenario component ID from model config
      const componentId = modelConfig.configs.scenario;

      // Prepare data payload for API
      const importData = {
        schoolKey: currentClientData.schoolKey,
        adjustedNonmodel: nonModeledData.adjusted, // Current adjusted values
        id: componentId,
      };

      // Create API promise for toast.promise handling
      let promise;
      promise = apiRoot.post(
        `/api/fao-scenarios/update-non-model/${componentId}`,
        importData
      );

      // Execute save with toast feedback
      await toast.promise(promise, toastMessages);

      // Update saved state to reflect successful save
      setIsSaved((prev) => ({ ...prev, nonModeledData: true }));
    } catch (error) {
      // Log error for debugging (toast.promise handles user notification)
      console.log(error);
    } finally {
      // Always clear saving state, regardless of success/failure
      setIsSaving((prev) => ({ ...prev, nonModeledData: false }));
    }
  };

  // Return all handler functions and utility functions for use in components
  return {
    handleNonModeledInputChange,
    isGroupAdjusted,
    isPopulationAdjusted,
    handleReset,
    handleResetAll,
    handleNonModeledSave,
  };
}

export default useNonModeledHandlers;
